import { Constructor } from '@skylab-shared/types';
import { deepEqual, isUndefined } from '@skylab-shared/utils';
import { DialogContentComponentBase } from '../dialog/dialog-content-base';
import { FilterChipCollectionItem } from './filter-chip/filter-chip-context';
import { DateRangeFilterChip } from './filter-chip/filter-chip-date-range/filter-chip-date-range.component';
import { DateFilterReturnValues } from './filter-chip/filter-chip-date-range/filter-chip-date-utils';
import { ManyFromListFilterChipComponent } from './filter-chip/filter-chip-many-from-list/filter-chip-many-from-list.component';
import { SklOneFromListFilterChipComponent } from './filter-chip/filter-chip-one-from-list/filter-chip-one-from-list.component';

export interface Chip {
	identityName: string;
	label: string;
	isHidden?: boolean;
	value: unknown;
	initialChipValue?: unknown;
	defaultValue?: unknown;
	onChange: (value: any) => void;
	component: Constructor<unknown>;
	anyItem?: boolean;
}

export function compareChipsValues(v: unknown, def: unknown): boolean {
	if (v === def) return false;

	const isEmpty = (val: unknown) => isUndefined(val) || val === null || val === '' || (Array.isArray(val) && val.length === 0);
	const vEmpty = isEmpty(v);
	const defEmpty = isEmpty(def);

	if (vEmpty && defEmpty) return false;
	if (vEmpty !== defEmpty) return true;

	return !deepEqual(v, def);
}

export function resetChips(chips: Chip[]) {
	chips?.forEach(chip => {
		chip.value = !isUndefined(chip.defaultValue) ? chip.defaultValue : chip.initialChipValue;
		chip.onChange(chip.value);
	});
}

export type ChipOptions = Omit<Chip, 'component' | 'initialChipValue'> & { component?: Constructor<unknown> };

export class Chip implements Chip {
	public identityName: string;
	public label: string;
	public isHidden?: boolean;
	public value: unknown;
	public initialChipValue?: unknown;
	public defaultValue?: unknown;
	public onChange: (value: any) => void;
	public component!: Constructor<unknown>;
	public anyItem?: boolean;

	constructor(config: ChipOptions) {
		this.identityName = config.identityName;
		this.label = config.label;
		this.isHidden = config.isHidden;
		this.value = config.value;
		this.defaultValue = config.defaultValue;
		this.onChange = config.onChange;
		this.anyItem = config.anyItem;
		if (config.component) {
			this.component = config.component;
		}
	}
}

export interface ChipListOptions extends ChipOptions {
	collection: FilterChipCollectionItem[] | null;
	customValueDialog?: Constructor<DialogContentComponentBase<any, any, any>>;
	customValueLabel?: string;
	customValueDisplayText?: (data: any) => string;
}

export interface ListChip extends Chip {
	collection: FilterChipCollectionItem[] | null;
}

class ListChipImpl extends Chip implements ListChip {
	public collection: FilterChipCollectionItem[] | null;
	constructor(config: ChipListOptions) {
		super(config);
		this.collection = config.collection;
	}
}

export interface OneFromListChip extends ListChip {
	customValueDialog?: Constructor<DialogContentComponentBase<any, any, any>>;
	customValueLabel?: string;
	customValueDisplayText?: (data: unknown) => string;
}

export class OneFromListChip extends ListChipImpl implements OneFromListChip {
	public override value: unknown;
	public override defaultValue?: unknown;
	public override initialChipValue?: unknown;
	public customValueDialog?: Constructor<DialogContentComponentBase<any, any, any>>;
	public customValueLabel?: string;
	public customValueDisplayText?: (data: unknown) => string;

	constructor(config: ChipListOptions) {
		super(config);
		this.component = SklOneFromListFilterChipComponent;
		this.customValueDialog = config.customValueDialog;
		this.customValueLabel = config.customValueLabel;
		this.customValueDisplayText = config.customValueDisplayText?.bind(this);
	}
}

export interface DateRangeOptions extends ChipOptions {
	value: DateFilterReturnValues | null;
	collection?: FilterChipCollectionItem[];
}

export interface DatesRangeChip extends Chip {
	value: DateFilterReturnValues | null;
	defaultValue?: DateFilterReturnValues | null;
	collection?: FilterChipCollectionItem[];
}

export class DatesRangeChip extends Chip implements DatesRangeChip {
	public override value!: DateFilterReturnValues | null;
	public override defaultValue?: DateFilterReturnValues | null;
	public override initialChipValue?: DateFilterReturnValues | null;
	public collection?: FilterChipCollectionItem[];
	constructor(config: DateRangeOptions) {
		super(config);
		this.component = DateRangeFilterChip;
		this.collection = config.collection;
	}
}

export interface ChipsManyFromListOptions extends ChipListOptions {

}

export interface ManyFromListChip extends ListChip {
	value: unknown[] | null;
	defaultValue?: unknown[] | null;
	initialChipValue?: unknown[] | null;
}

export class ManyFromListChip extends ListChipImpl implements ManyFromListChip {
	public override value!: unknown[] | null;
	public override defaultValue?: unknown[] | null;
	public override initialChipValue?: unknown[] | null;
	public limit?: number;
	static defaultLimit = 1 as const;

	constructor(config: ChipsManyFromListOptions) {
		super(config);
		this.limit = ManyFromListChip.defaultLimit;
		this.component = ManyFromListFilterChipComponent;
	}
}
